import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Typography } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';

import HistoryEntry from './HistoryEntry';
import RevertChangeModal from './RevertChangeModal';

const { Text } = Typography;

/**
 * A pageable list of history entries over any of the history endpoints.
 * `fetchPage({ limit, skip, ...filters })` resolves `{ rows, total }`; the
 * list renders newest first, appends on "Load more", and reloads from the top
 * after a revert (the reverting entry is the newest row). Filters are the
 * caller's — a feed's toolbar passes them in and changing them restarts the
 * list.
 *
 *   <HistoryList fetchPage={p => fetchItemHistory(itemId, p)} />
 *
 * `onOpenItemHistory(entry)` is a feed's (a collection's History tab): it
 * offers **View history** on an item's deletion (HistoryEntry).
 */
export default function HistoryList({
	fetchPage,
	filters = {},
	pageSize = 25,
	canRevert = true,
	showTarget = true,
	compact = false,
	emptyText = 'No changes recorded yet.',
	onCountChange,
	onReverted,
	onOpenItemHistory,
	reloadKey = 0,
	className = '',
}) {
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	// Whether a first read of this list has landed: after it, a reload is a refresh and keeps the rows.
	const [hasData, setHasData] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [reverting, setReverting] = useState(null);
	const [generation, setGeneration] = useState(0);
	const [attempt, setAttempt] = useState(0);

	const filterKey = JSON.stringify(filters || {});

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError(null);
		fetchPage({ ...filters, limit: pageSize, skip: 0 })
			.then(({ rows: page, total: count }) => {
				if (!active) return;
				setRows(page || []);
				setTotal(count || 0);
				setError(null);
				setHasData(true);
				if (typeof onCountChange === 'function') onCountChange(count || 0);
			})
			.catch(err => { if (active) setError(err); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	// filters is compared by value through filterKey.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchPage, filterKey, pageSize, reloadKey, generation, attempt]);

	const loadMore = useCallback(async () => {
		setLoadingMore(true);
		try {
			const { rows: page, total: count } = await fetchPage({ ...filters, limit: pageSize, skip: rows.length });
			setRows(prev => [...prev, ...(page || [])]);
			setTotal(count || 0);
		} catch (err) {
			setError(err);
		} finally {
			setLoadingMore(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchPage, filterKey, pageSize, rows.length]);

	const handleReverted = useCallback((result, notice) => {
		setGeneration(g => g + 1);
		if (typeof onReverted === 'function') onReverted(result, notice);
	}, [onReverted]);

	const status = error?.response?.status;

	return (
		<div className={['history-list', compact ? 'history-list--compact' : '', className].filter(Boolean).join(' ')}>
			{/* Someone who may not read history is told so; it is not a failed load. */}
			{!loading && error && status === 403 ? (
				<Alert type="info" showIcon message="History is available to people who can edit this record." />
			) : (
				<DataState
					state={deriveDataState({ loading, error, hasData, isEmpty: rows.length === 0 })}
					hasData={hasData}
					isEmpty={rows.length === 0}
					skeleton={<Skeleton variant="list-row" count={3} />}
					empty={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />}
					error={error}
					onRetry={() => setAttempt(n => n + 1)}
					label="history"
				>
					<ol className="history-list__entries">
						{rows.map(entry => (
							<li key={entry._id}>
								<HistoryEntry
									entry={entry}
									showTarget={showTarget}
									compact={compact}
									onRevert={canRevert ? setReverting : undefined}
									onOpenItemHistory={onOpenItemHistory}
								/>
							</li>
						))}
					</ol>
				</DataState>
			)}

			{!loading && !error && rows.length < total ? (
				<div className="history-list__more">
					<Button onClick={loadMore} loading={loadingMore}>Load more</Button>
					<Text type="secondary">{`${rows.length} of ${total}`}</Text>
				</div>
			) : null}

			<RevertChangeModal
				open={Boolean(reverting)}
				entry={reverting}
				onClose={() => setReverting(null)}
				onReverted={handleReverted}
			/>
		</div>
	);
}
